/* ============================================================================
   Request detail — the host page for the request-detail TAB registry.
   Renders the request header + triage actions (assign / accept / status), then
   hosts every tab registered via registerRequestTab() (Overview ships here;
   Proofing / Hours / Comments register their own from their own modules).
   ============================================================================ */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiGet, apiPut, apiDelete, isApiError } from '../../lib/api';
import { useAuth, hasRole } from '../../lib/auth';
import { getRequestTabs } from '../../app/requestTabs';
import {
  useLookups,
  statusPillClass,
  priorityPillClass,
  type RequestDetail as Detail,
} from './data';

interface Assignee {
  id: number;
  name: string;
  email: string;
}

function Meta({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <span className="mono" style={{ fontSize: 'var(--fs-xs)', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--ink-3)' }}>
        {label}
      </span>
      <span style={{ fontSize: 'var(--fs-sm)' }}>{children}</span>
    </div>
  );
}

export default function RequestDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { lookups } = useLookups();
  const requestId = Number(id);

  const [detail, setDetail] = useState<Detail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [assignees, setAssignees] = useState<Assignee[]>([]);
  const [assignTo, setAssignTo] = useState('');
  const [assignPriority, setAssignPriority] = useState('');
  const [statusChoice, setStatusChoice] = useState('');
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (!Number.isFinite(requestId)) return;
    setLoading(true);
    apiGet<Detail>(`/api/requests/${requestId}`)
      .then((d) => {
        setDetail(d);
        setStatusChoice(d.statusCode);
        setAssignTo(d.designerId ? String(d.designerId) : '');
        setError(null);
      })
      .catch((e) => setError(isApiError(e) ? e.message : 'Could not load this request.'))
      .finally(() => setLoading(false));
  }, [requestId]);

  useEffect(() => {
    load();
  }, [load]);

  const canAssign = hasRole(user, ['Admin', 'SystemAdmin']);
  const isAssignedDesigner = user?.role === 'Designer' && detail?.designerId === user.id;
  const canSetStatus = canAssign || isAssignedDesigner;

  // Load the designer picker only for those who can assign.
  useEffect(() => {
    if (!canAssign) return;
    apiGet<Assignee[]>('/api/requests/meta/assignees')
      .then(setAssignees)
      .catch(() => undefined);
  }, [canAssign]);

  const tabs = useMemo(() => getRequestTabs().filter((t) => hasRole(user, t.roles)), [user]);
  const [active, setActive] = useState('');
  useEffect(() => {
    if (tabs.length && !tabs.some((t) => t.id === active)) setActive(tabs[0].id);
  }, [tabs, active]);
  const ActiveComponent = tabs.find((t) => t.id === active)?.component ?? tabs[0]?.component;

  async function runAction(fn: () => Promise<unknown>, okMsg: string): Promise<void> {
    setBusy(true);
    setActionMsg(null);
    try {
      await fn();
      setActionMsg(okMsg);
      load();
    } catch (e) {
      setActionMsg(isApiError(e) ? e.message : 'Action failed.');
    } finally {
      setBusy(false);
    }
  }

  async function deleteRequest(): Promise<void> {
    if (!detail) return;
    if (
      !window.confirm(
        `Permanently delete ${detail.ticketId} — "${detail.title}"? This removes all its ` +
          `proofs, comments and hours, and cannot be undone.`,
      )
    ) {
      return;
    }
    setBusy(true);
    setActionMsg(null);
    try {
      await apiDelete(`/api/requests/${detail.id}`);
      navigate('/requests');
    } catch (e) {
      setActionMsg(isApiError(e) ? e.message : 'Could not delete this request.');
      setBusy(false);
    }
  }

  async function cancelRequest(): Promise<void> {
    if (!detail) return;
    if (!window.confirm('Cancel this request? Its status becomes "Canceled" — it is not deleted.')) {
      return;
    }
    await runAction(() => apiPut(`/api/requests/${detail.id}/cancel`, {}), 'Request canceled.');
  }

  if (loading) {
    return (
      <div className="container page">
        <div className="card" style={{ display: 'grid', placeItems: 'center', padding: 48 }}>
          <div className="spinner" />
        </div>
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div className="container page">
        <button className="btn btn-ghost btn-sm" onClick={() => navigate('/requests')}>
          ← All requests
        </button>
        <div className="card empty" style={{ marginTop: 16 }}>
          <h3>Couldn't open this request</h3>
          <p className="muted">{error ?? 'Not found.'}</p>
        </div>
      </div>
    );
  }

  const ownerDraft = detail.isDraft === 1 && detail.requesterId === user?.id;
  const isOwnerRequester = user?.role === 'Requester' && detail.requesterId === user.id;
  const canCancel =
    (isOwnerRequester || canAssign) &&
    detail.isDraft !== 1 &&
    detail.statusCode !== 'canceled' &&
    detail.statusCode !== 'complete';

  return (
    <div className="container page">
      <div className="page-title-row">
        <div>
          <button className="btn btn-ghost btn-sm" onClick={() => navigate('/requests')}>
            ← All requests
          </button>
          <div className="row" style={{ gap: 10, marginTop: 8 }}>
            <span className="mono" style={{ color: 'var(--ink-3)' }}>{detail.ticketId}</span>
            <span className={`pill ${statusPillClass(detail.statusCode)}`}>{detail.statusName}</span>
            {detail.isDraft === 1 && <span className="pill pill-warning">Draft</span>}
            {detail.priorityCode &&
              (priorityPillClass(detail.priorityCode) ? (
                <span className={`pill ${priorityPillClass(detail.priorityCode)}`}>{detail.priorityName}</span>
              ) : (
                <span className="meta">{detail.priorityName} priority</span>
              ))}
          </div>
          <h1 style={{ marginTop: 8, marginBottom: 0 }}>{detail.title}</h1>
        </div>
        <div className="row" style={{ gap: 8 }}>
          {ownerDraft && (
            <button className="btn btn-navy" onClick={() => navigate(`/requests/${detail.id}/edit`)}>
              Edit draft
            </button>
          )}
          {canCancel && (
            <button
              className="btn btn-ghost"
              disabled={busy}
              title="Cancel this request (does not delete it)"
              onClick={cancelRequest}
            >
              Cancel request
            </button>
          )}
          {canAssign && (
            <button
              className="btn btn-ghost"
              style={{ color: '#b3261e' }}
              disabled={busy}
              title="Permanently delete this request"
              onClick={deleteRequest}
            >
              Delete
            </button>
          )}
        </div>
      </div>

      {/* Summary meta strip */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
          <Meta label="Requester">{detail.requesterName}</Meta>
          <Meta label="Organization">{detail.orgName ?? '—'}</Meta>
          <Meta label="Designer">{detail.designerName ?? <span className="muted">Unassigned</span>}</Meta>
          <Meta label="Requested">{detail.requestDate ?? '—'}</Meta>
          <Meta label="Needed by">{detail.neededBy ?? '—'}</Meta>
          <Meta label="Completed">{detail.actualCompletion ?? '—'}</Meta>
        </div>
      </div>

      {/* Triage actions — staff only */}
      {canSetStatus && (
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="card-header">
            <h3>Triage</h3>
          </div>

          <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
            {canAssign && (
              <div>
                <div className="field" style={{ marginBottom: 8 }}>
                  <label>Assign designer</label>
                  <select className="in" value={assignTo} onChange={(e) => setAssignTo(e.target.value)}>
                    <option value="">Select designer…</option>
                    {assignees.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="field" style={{ marginBottom: 8 }}>
                  <label>
                    Set priority <span className="muted">(optional)</span>
                  </label>
                  <select className="in" value={assignPriority} onChange={(e) => setAssignPriority(e.target.value)}>
                    <option value="">Keep current</option>
                    {lookups.priorities.map((p) => (
                      <option key={p.code} value={p.code}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  className="btn btn-navy btn-sm"
                  disabled={busy || !assignTo}
                  onClick={() =>
                    runAction(
                      () =>
                        apiPut(`/api/requests/${detail.id}/assign`, {
                          designerId: Number(assignTo),
                          priorityCode: assignPriority || undefined,
                        }),
                      'Designer assigned.',
                    )
                  }
                >
                  Assign
                </button>
              </div>
            )}

            <div>
              <div className="field" style={{ marginBottom: 8 }}>
                <label>Design status</label>
                <select className="in" value={statusChoice} onChange={(e) => setStatusChoice(e.target.value)}>
                  {lookups.statuses.map((s) => (
                    <option key={s.code} value={s.code}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="row" style={{ gap: 8 }}>
                <button
                  className="btn btn-navy btn-sm"
                  disabled={busy || statusChoice === detail.statusCode}
                  onClick={() =>
                    runAction(
                      () => apiPut(`/api/requests/${detail.id}/status`, { statusCode: statusChoice }),
                      'Status updated.',
                    )
                  }
                >
                  Update status
                </button>
                {isAssignedDesigner && detail.statusCode === 'new' && (
                  <button
                    className="btn btn-primary btn-sm"
                    disabled={busy}
                    onClick={() =>
                      runAction(() => apiPut(`/api/requests/${detail.id}/accept`, {}), 'Request accepted.')
                    }
                  >
                    Accept &amp; start
                  </button>
                )}
              </div>
            </div>
          </div>

          {actionMsg && (
            <p className="meta" style={{ marginTop: 12 }}>
              {actionMsg}
            </p>
          )}
        </div>
      )}

      {/* Tab registry host */}
      {tabs.length > 0 ? (
        <>
          <div className="tabs" role="tablist">
            {tabs.map((t) => (
              <button
                key={t.id}
                role="tab"
                aria-selected={active === t.id}
                className={`tab ${active === t.id ? 'tab-active' : ''}`}
                onClick={() => setActive(t.id)}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div>{ActiveComponent && <ActiveComponent requestId={requestId} />}</div>
        </>
      ) : (
        <div className="empty card">
          <h3>No detail tabs available</h3>
          <p className="muted">No modules have registered a tab for your role.</p>
        </div>
      )}
    </div>
  );
}
